import { StateParams } from '@uirouter/core';
import * as angular from 'angular';
import { taOptions } from 'textangular';
import * as _ from 'underscore';
import { WysiwygControllerBase } from '../../../app.common/modules/common/controllers/wysiwygControllerBase';
import { PopupServiceCancel } from '../../../app.common/modules/common/models/popupServiceCancel';
import { DefinitionsService } from '../../../app.common/modules/common/services/definitions.service';
import { HtmlService } from '../../../app.common/modules/common/services/html.service';
import { NotificationService } from '../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../app.common/modules/common/services/popup.service';
import { WysiwygService } from '../../../app.common/modules/common/services/wysiwyg.service';
import { PreviousStateService } from '../../../app.common/modules/routes/previous-state.service';
import { BrandsService } from '../brands/brands.service';
import { CategoriesSelectorService } from '../categories/categoriesSelector.service';
import { NfcStickerProductItemModel, PlaceListShortModelPagingResult, StoreFindFilter } from '../core';
import { IRetailerRootScope } from '../core/models';
import { NfcRetailerStickersService } from '../nfc-stickers/nfc-retailer-stickers.service';
import { IStoresListItem } from '../products/productBase.controller';
import { INfcStickersErrors } from './../products/models/index';
import { InformationCardsService } from './informationCards.service';
import { IInformationCard, ILinkListItem, IPhysicalProductLink } from './models';

interface IProductState {
    isEdit: boolean;
    isCopy: boolean;
    isNew: boolean;
    saveTitle: string;
}

export abstract class InformationCardBaseController extends WysiwygControllerBase {

    stores: IStoresListItem[];
    canEditStickers: boolean;

    waiting = false;
    state: IProductState = null;
    productForm: ng.IFormController;
    model = {
        main: <IInformationCard>null,
        edit: <IInformationCard>null,
        editSample: null,
        linkTypes: <ILinkListItem[]>[],
        serverImagesID: <string[]>[],
        draft: {
            fields: {
                images: { state: false, name: this.$translate.instant('information_card_image') }
            },
            setAll: (state: boolean) => {
                let i = this.model.draft;
                _.forEach(i.fields, (field) => {
                    field.state = state;
                });
            },
            verify: (product: IInformationCard) => {
                let i = this.model.draft;

                i.fields.images.state = _.size(product.imagesID) === 0;
            },
            getDraftFields: (product: IInformationCard) => {
                var i = this.model.draft;
                i.verify(product);
                return _.filter(i.fields, (field) => {
                    return field.state;
                });
            }
        },
        fillServerImagesID: () => {
            let i = this.model;
            i.serverImagesID = [];
            if (this.state.isEdit && i.main) {
                i.serverImagesID = angular.copy(i.main.imagesID);
            }
        },
        getServerVariationImagesID: (id: string) => {
            let i = this.model;
            let variationID = parseInt(<string>id, 10);

            if (i.main && i.main.imagesID) {
                i.serverImagesID = angular.copy(i.edit.imagesID);
                return i.serverImagesID;
            }
            return <string[]>[];
        },
        getClientImagesID: () => {
            let i = this.model;
            if (i.edit && i.edit.imagesID) {
                return <string[]>angular.copy(i.edit.imagesID);
            }
            return <string[]>[];
        },
        fillEditModel: () => {
            var i = this.model;
            i.edit = angular.copy(i.main);
            i.fillServerImagesID();

            _.forEach(i.edit.links, (link) => {
                link.type = _.findWhere(i.linkTypes, { ID: link.Type });
            });
            i.editSample = angular.copy(i.edit);
        },
        isValidModel: async() => {
            let i = this.model;
            let model = i.edit;
            let valid = await i.isValidProduct();
            if (valid) {
                model.error.summary = null;
            } else {
                model.error.summary = this.$translate.instant('form_has_error');
            }
            return valid;
        },
        isValidProduct: async() => {
            let model = this.model.edit;
            // title
            model.title = model.title ? model.title.trim() : '';
            if (model.title.length === 0) {
                model.error.title = this.requiredfield;
            } else if (model.title.length > 128) {
                model.error.title = this.$translate.instant('title_too_long');
            } else {
                model.error.title = null;
            }

            // description
            model.description = model.description ? model.description.trim() : '';
            if (model.description.length > 30000) {
                model.error.description = this.$translate.instant('description_too_long', { maxLength: 30000 });
            } else {
                model.error.description = null;
            }

            // stickers
            let stickersError = await this.model.isValidStickers();

            return model.error.title === null
                && model.error.description == null && this.model.isValidLinks() && stickersError;
        },
        isValidStickers: async() => {
            this.model.edit.error.stickers = [];
            let editModel = this.model.edit;
            if (!editModel.nfcStickers || (editModel.nfcStickers && editModel.nfcStickers.length === 0)) {
                return this.$q.when(true);
            }

            const STICKER_NUMBER_PATTERN = new RegExp('[A-Z]{1}\\d{12}$');

            let stickersError = <boolean>false;
            let duplicatesMap = _.groupBy(editModel.nfcStickers, (item) => item.StickerNumber);

            editModel.nfcStickers.forEach((sticker, index) => {
                let stickerHasError = <boolean>false;
                let stickerError = {
                        id: index,
                        nfcNumber: null,
                        store: null
                    } as INfcStickersErrors;

                if (!sticker.StickerNumber) {
                    stickerHasError = true;
                    stickerError.nfcNumber = this.$translate.instant('nfc_product_stickers_list_sticker_required_error');
                }

                if (sticker.StickerNumber) {
                    if (!STICKER_NUMBER_PATTERN.test(sticker.StickerNumber)) {
                        stickerHasError = true;
                        stickerError.nfcNumber = this.$translate.instant('nfc_product_stickers_list_sticker_format_error');
                    } else {
                        let duplicates = duplicatesMap[sticker.StickerNumber];
                        if (duplicates && duplicates.length > 1) {
                            stickerHasError = true;
                            stickerError.nfcNumber = this.$translate.instant('sticker_is_already_used');
                        }
                    }
                }

                if (!sticker.PlaceID) {
                    stickerHasError = true;
                    stickerError.store = this.$translate.instant('nfc_product_stickers_list_location_required_error');
                }
                if (stickerHasError) {
                    this.model.edit.error.stickers.push(stickerError);
                    stickersError = true;
                }
            });

            if (stickersError) {
                return this.$q.when(false);
            }

            return this.$q.when(true);
        },
        isValidLink: (link: IPhysicalProductLink) => {
            link.error = { sum: null };
            link.Title = link.Title ? link.Title.trim() : '';
            if (link.Title.length === 0) {
                link.error.title = this.$translate.instant('title_is_required');
            } else if (link.Title.length > 64) {
                link.error.title = this.$translate.instant('title_too_long');
            } else {
                link.error.title = null;
            }

            let regexUrl = this.definitionsService.regexUrl;
            link.Url = link.Url ? link.Url.trim() : '';
            if (link.Url.length === 0) {
                link.error.url = this.$translate.instant('web_address_is_required');
            } else if (!regexUrl.test(link.Url)) {
                link.error.url = this.$translate.instant('web_address_is_invalid');
            } else if (link.Url.length > 256) {
                link.error.url = this.$translate.instant('web_address_too_long');
            } else {
                link.error.url = null;
            }

            if (!link.type) {
                link.error.type = this.$translate.instant('link_type_url_title_are_required');
            } else {
                link.error.type = null;
            }

            if (link.error.type) {
                link.error.sum = link.error.type;
            } else if (link.error.url) {
                link.error.sum = link.error.url;
            } else if (link.error.title) {
                link.error.sum = link.error.title;
            }
            return !link.error.sum;
        },
        isValidLinks: () => {
            let i = this.model;
            let isValid = true;
            if (_.all(i.edit.links, i.isValidLink)) {
                // url uniq
                _.forEach(i.edit.links, (current) => {
                    if (isValid) {
                        var eqUrl = _.where(i.edit.links, { Url: current.Url });
                        if (_.size(eqUrl) > 1) {
                            isValid = false;
                            _.forEach(eqUrl, (link) => {
                                link.error.sum = this.$translate.instant('web_address_must_be_unique');
                                link.error.url = this.$translate.instant('web_address_must_be_unique');
                            });
                        }
                    }
                });
                return isValid;
            }
            return false;
        },
        loadedModel: () => { return; }
    };

    private requiredfield = this.$translate.instant('required_field');
    private productID: string = null;

    constructor(
        protected $rootScope: IRetailerRootScope,
        protected $scope: ng.IScope,
        protected $q: ng.IQService,
        protected $stateParams: StateParams,
        protected $timeout: ng.ITimeoutService,
        protected $previousState: PreviousStateService,
        protected informationCardsService: InformationCardsService,
        protected categoriesSelectorService: CategoriesSelectorService,
        protected brandsService: BrandsService,
        protected popupService: PopupService,
        protected taOptions: taOptions,
        protected htmlService: HtmlService,
        protected definitionsService: DefinitionsService,
        protected wysiwygService: WysiwygService,
        protected notificationService: NotificationService,
        protected $translate: angular.translate.ITranslateService,
        protected nfcRetailerStickersService: NfcRetailerStickersService,
    ) {
        super($scope, taOptions, wysiwygService);
        this.productID = this.$stateParams.id;
        this.state = {
            isEdit: (this.productID) && $rootScope.$state.current.name.startsWith('app.information-card.edit'),
            isCopy: (this.productID) && $rootScope.$state.current.name.startsWith('app.information-card.copy'),
            isNew: $rootScope.$state.current.name.startsWith('app.information-card.new'),
            saveTitle: (this.productID) && $rootScope.$state.current.name.startsWith('app.information-card.edit')
                ? this.$translate.instant('save')
                : this.$translate.instant('create')
        };
        this.init();
    }

    discard = () => {
        this.deleteImages();
        this.back();
    }

    getImageUrl = (imageIDs: string[] | string) => {
        if (!imageIDs) {
            return null;
        }
        if (_.isArray(imageIDs)) {
            return this.informationCardsService.getImageUrl(_.first(imageIDs));
        } else {
            return this.informationCardsService.getImageUrl(imageIDs);
        }
    }

    deleteImage = async (id: string) => {
        let imageID = id;
        try {

            if ((imageID) || _.contains(this.model.serverImagesID, imageID)) {
                return;
            }

            let ok = await this.informationCardsService.deleteImage(imageID);
            if (ok) {
                console.log('Deleted imageID - ' + imageID);
            } else {
                console.log('Could not remove image with id - ' + imageID);
            }
        } catch (error) {
            console.error(error + ' Image with id ' + imageID + ' is not removed');
        }
    }

    back = () => {
        var pSt = this.$previousState.get();
        var stateNameProducts = 'app.information-cards';
        if (this.state.isEdit && pSt && pSt.state.name === stateNameProducts && pSt.params && pSt.params.page) {
            this.$rootScope.$state.go(stateNameProducts, { page: pSt.params.page, search: pSt.params.search, column: pSt.params.column, order: pSt.params.order });
        } else {
            this.$rootScope.$state.go(stateNameProducts);
        }
    }

    save = async () => {
        if (!(await this.model.isValidModel())) {
            return;
        }
        let draftFields = this.model.draft.getDraftFields(this.model.edit);
        try {
            if (_.size(draftFields) > 0) {
                let msg = `<p>${this.$translate.instant('save_information_card_as_draft_confirm')}</p>`;
                let li = '';
                _.forEach(draftFields, (field) => {
                    li += '<li>' + field.name + '</li>';
                });
                let ul = '<ul class="pl15">' + li + '</ul>';
                await this.popupService.confirm({
                    title: this.$translate.instant('warning'),
                    content: msg + ul,
                    okTxt: this.$translate.instant('save_draft'),
                    cancelTxt: this.$translate.instant('complete_the_fields'),
                    okClass: 'btn btn-success plr15',
                    cancelClass: 'btn btn-danger plr15'
                });
            }
            this.$save();
        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClickInstance(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    addLink = () => {
        if (!this.model.isValidLinks()) {
            return;
        }
        var link = {
            type: null,
            Title: '',
            Url: '',
            error: null
        };
        this.model.edit.links.push(link);
        this.productForm.$setDirty();
    }

    removeLink = (index: number) => {
        this.model.edit.links.splice(index, 1);
        this.productForm.$setDirty();
    }

    pasteHandler = (html: string) => {
        return this.htmlService.filterWysiwygContent(html);
    }

    protected updateVariationPreview = () => {
        return;
    }

    private deleteImages = () => {
        _.forEach(this.model.getClientImagesID(), (id) => {
            this.deleteImage(id);
        });
        this.productForm.$setDirty();
    }

    private sortImageID = (card: IInformationCard) => {
        card.imagesID.sort();
    }

    private $save = async () => {
        try {
            this.saveInternal();

        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClick(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    private saveInternal = async () => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        try {
            let model = angular.copy(this.model.edit);
            if (this.state.isEdit) {
                await this.informationCardsService.updateInformationCard(this.productID, model);
            } else {
                await this.informationCardsService.createInformationCard(model);
            }
            this.instanceWysiwygService.deleteRemovedTokens(model.description);
            this.notificationService.showSuccessMessage();
            this.$timeout(this.back, 1000);
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    private init = async () => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        try {
            let getProduct = this.state.isEdit || this.state.isCopy ?
                this.informationCardsService.getInformationCardModel(this.productID, this.state.isEdit) :
                this.informationCardsService.getDefaultProductModel();
            // TODO: change end-point
            let getLocations = this.nfcRetailerStickersService.getLocations({});
            let checkCanEditStickers = this.nfcRetailerStickersService.checkCanEditStickers();
            let response = await  this.$q.all([
                <angular.IPromise<IInformationCard>>getProduct,
                <angular.IPromise<ILinkListItem[]>>this.informationCardsService.getLinkTypes(),
                <angular.IPromise<PlaceListShortModelPagingResult>>getLocations,
                <angular.IPromise<boolean>>checkCanEditStickers]);
            this.model.main = response[0];
            this.model.linkTypes = response[1];
            this.stores = response[2].Items;
            let canEditStickers = response[3];
            this.canEditStickers = this.stores.length !== 0 && canEditStickers;
            this.model.fillEditModel();
            this.model.loadedModel();

            this.notificationService.clearAll();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/informationCards/informationCardBase.controller.ts